import type { IteroAssetResult, IteroAssetType } from '../../../../services/iteroBrowse';

export interface AssetOption {
    type: IteroAssetType;
    label: string;
    slug: string;
    errorText: string;
}

export const ASSET_OPTIONS: readonly AssetOption[] = [
    {
        type: 'IDE',
        label: '3D files',
        slug: '3d-files',
        errorText: "Couldn't download the 3D files"
    },
    {
        type: 'IMAGE_GALLERY',
        label: 'Model images',
        slug: 'model-images',
        errorText: "Couldn't download the model images"
    },
    {
        type: 'SCREEN_CAPTURE',
        label: 'Screen captures',
        slug: 'screen-captures',
        errorText: "Couldn't download the screen captures"
    }
];

export type AssetDownloadState =
    | { status: 'idle' }
    | { status: 'downloading' }
    | { status: 'saved' }
    | { status: 'generating' }
    | { status: 'empty' }
    | { status: 'error'; message: string };

function stateForResult(result: IteroAssetResult): AssetDownloadState {
    switch (result.kind) {
        case 'ready':
            return { status: 'saved' };
        case 'generating':
            return { status: 'generating' };
        case 'empty':
            return { status: 'empty' };
    }
}

export interface DownloadProgress {
    isPending: boolean;
    errorMessage: string | null;
    result: IteroAssetResult | undefined;
}

export function downloadState({
    isPending,
    errorMessage,
    result
}: DownloadProgress): AssetDownloadState {
    if (isPending) return { status: 'downloading' };
    if (errorMessage !== null) return { status: 'error', message: errorMessage };
    return result ? stateForResult(result) : { status: 'idle' };
}

const EXTENSION_BY_CONTENT_TYPE: Readonly<Record<string, string>> = {
    'application/zip': 'zip',
    'application/x-zip-compressed': 'zip',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'application/pdf': 'pdf',
    'model/stl': 'stl'
};
const DEFAULT_EXTENSION = 'zip';
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|]+/g;
const FIRST_PRINTABLE_CHAR_CODE = 32;
const MAX_FILENAME_LENGTH = 180;

export function sanitizeFilename(name: string): string {
    const printable = [...name]
        .filter((char) => char.charCodeAt(0) >= FIRST_PRINTABLE_CHAR_CODE)
        .join('');
    return printable.replace(UNSAFE_FILENAME_CHARS, '_').trim().slice(0, MAX_FILENAME_LENGTH);
}

function decodeExtendedValue(value: string): string | null {
    const encoded = value.replace(/^[\w-]+'[\w-]*'/, '');
    try {
        return decodeURIComponent(encoded);
    } catch {
        return null;
    }
}

// RFC 6266: prefer filename* (percent-encoded UTF-8) over the plain filename.
export function filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const extended = /filename\*\s*=\s*([^;]+)/i.exec(header);
    const extendedName = extended
        ? decodeExtendedValue(extended[1].trim().replace(/^"|"$/g, ''))
        : null;
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    const plainName = plain ? (plain[2] ?? plain[1]).trim() : null;
    const name = sanitizeFilename(extendedName ?? plainName ?? '');
    return name || null;
}

export function fallbackAssetFilename(
    orderCode: string,
    assetType: IteroAssetType,
    contentType: string | null
): string {
    const option = ASSET_OPTIONS.find((candidate) => candidate.type === assetType);
    const mime = contentType?.split(';')[0].trim().toLowerCase() ?? '';
    const extension = EXTENSION_BY_CONTENT_TYPE[mime] ?? DEFAULT_EXTENSION;
    const code = sanitizeFilename(orderCode) || 'order';
    return `itero-${code}-${option?.slug ?? 'files'}.${extension}`;
}

export function assetFilename(
    result: { contentDisposition: string | null; contentType: string | null },
    orderCode: string,
    assetType: IteroAssetType
): string {
    return (
        filenameFromContentDisposition(result.contentDisposition) ??
        fallbackAssetFilename(orderCode, assetType, result.contentType)
    );
}
